import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Driver, Alert, Delivery, CargoItem, RouteStop } from '../data/mockData';
import { Truck, MapPin, Clock, Phone, Navigation, AlertTriangle, ShieldAlert, PackageIcon, Check, ChevronDown, MoreVertical, ListOrdered } from 'lucide-react';
import { ReroutePanel } from './ReroutePanel';
import { SuggestRoutePanel } from './SuggestRoutePanel';
import { RouteSuggestion } from './SuggestRoutePanel';
interface DriverDetailProps {
  driver: Driver | null;
  alerts: Alert[];
  deliveries?: Delivery[];
  scrollToRouteTs?: number | null;
  selectedDeliveryRef?: string | null;
  onContactDriver?: (driverId: string) => void;
  onResolveAlert?: (alertId: string) => void;
  onDismissAlert?: (alertId: string) => void;
  onUpdateRoute?: (driverId: string, stops: RouteStop[]) => void;
  onPreviewRoute?: (driverId: string, stops: RouteStop[]) => void;
  onCancelRoutePreview?: (driverId: string) => void;
  onApplyReroute?: (driverId: string, suggestion: {
    etaImpactMin: number;
    label: string;
  }) => void;
  onRerouteToggle?: (isOpen: boolean) => void;
  onHoverRoute?: (routeId: number | null) => void;
  customSuggestions?: RouteSuggestion[];
}
export function DriverDetail({
  driver,
  alerts,
  deliveries = [],
  scrollToRouteTs,
  selectedDeliveryRef,
  onContactDriver,
  onResolveAlert,
  onDismissAlert,
  onUpdateRoute,
  onPreviewRoute,
  onCancelRoutePreview,
  onApplyReroute,
  onRerouteToggle,
  onHoverRoute,
  customSuggestions
}: DriverDetailProps) {
  const routeRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [openDropdownAlertId, setOpenDropdownAlertId] = useState<string | null>(null);
  const [confirmDismissAlertId, setConfirmDismissAlertId] = useState<string | null>(null);
  const [isRerouteOpen, setIsRerouteOpen] = useState(false);
  const [isSuggestRouteOpen, setIsSuggestRouteOpen] = useState(false);
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownAlertId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  useEffect(() => {
    if (scrollToRouteTs && routeRef.current && scrollContainerRef.current) {
      const timer = setTimeout(() => {
        routeRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [scrollToRouteTs]);
  // Reset reroute panel when driver changes
  useEffect(() => {
    setIsRerouteOpen(false);
    setIsSuggestRouteOpen(false);
  }, [driver?.id]);
  useEffect(() => {
    onRerouteToggle?.(isSuggestRouteOpen);
  }, [isSuggestRouteOpen, onRerouteToggle]);
  if (!driver) {
    return null;
  }
  // Build a map from stop address to full delivery object
  const stopDeliveryMap = new Map<string, Delivery>();
  deliveries.forEach(d => {
    stopDeliveryMap.set(d.destination, d);
  });
  const hosPercent = driver.hosDriven / 11 * 100;
  const isHosCritical = driver.hosDriven > 10;
  const isOffline = driver.status === 'offline';
  return <motion.div initial={{
    x: 380,
    opacity: 0
  }} animate={{
    x: 0,
    opacity: 1
  }} exit={{
    x: 380,
    opacity: 0
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 250
  }} style={driver.signalBars === 0 ? {
    border: '2px solid #8EA0B6'
  } : undefined} className={`${`${`w-[380px] h-full glass-panel flex flex-col shrink-0 z-10 relative bg-slate-900/90 ${driver.signalBars === 0 ? '' : 'border-l border-slate-800'}`}`} bg-slate-950/90`} data-id="element-209">
      {/* Offline Banner */}
      {driver.signalBars === 0 && <div className="px-4 py-2.5 border-b border-[#8EA0B6] flex items-center justify-between shrink-0 bg-[#8EA0B6]" data-id="element-210">
          <div className="flex items-center gap-2 text-slate-900" data-id="element-211">
            <img src="https://cdn.magicpatterns.com/uploads/qtiWE7RgqX6uR9cCddEQgD/android_cell_4_bar_off_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24.svg" alt="No signal" className="w-4 h-4 brightness-0" data-id="element-212" />
            <span className="text-xs font-semibold" data-id="element-213">Truck Offline</span>
          </div>
          <span className="text-[10px] font-mono font-medium text-slate-900/60" data-id="element-214">
            LAST UPDATE 4 MIN AGO
          </span>
        </div>}

      {/* Header - Truck ID only (sticky) */}
      <div className="px-5 py-3 bg-slate-950/50 border-b border-slate-800 relative overflow-hidden shrink-0 z-20" data-id="element-215">
        <div className="flex justify-between items-center relative z-10" data-id="element-216">
          <h2 className="text-base font-bold text-slate-100 font-mono tracking-tight flex items-center gap-2" data-id="element-217">
            <Truck className="w-4 h-4 text-slate-500" data-id="element-218" />
            {driver.truckId}
            <SignalBars bars={driver.signalBars ?? 4} data-id="element-219" />
          </h2>
          <StatusBadge status={driver.status} data-id="element-220" />
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden flex flex-col" data-id="element-221">
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto scrollbar-hide" data-id="element-222">
          {/* Driver Info (scrollable) */}
          <div className="px-5 pt-4 pb-3 border-b border-slate-800" data-id="element-223">
            <div className="flex items-center gap-2 text-sm text-slate-400 mb-3" data-id="element-224">
              <span data-id="element-225">{driver.name}</span>
              <span data-id="element-226">•</span>
              <span data-id="element-227">Clearance: {driver.truckHeight}</span>
            </div>

            {/* Condensed HOS Bar */}
            <div className="relative z-10" data-id="element-228">
              <div className="flex items-center justify-between mb-1.5" data-id="element-229">
                <span className="text-[10px] text-slate-500 flex items-center gap-1.5" data-id="element-230">
                  <Clock className="w-3 h-3" data-id="element-231" />
                  HOS
                </span>
                <span className="text-[10px] font-mono text-slate-400" data-id="element-232">
                  {driver.hosDriven.toFixed(1)} / 11.0 hrs
                  {isHosCritical && <span className="text-brand-red ml-1.5 font-semibold" data-id="element-233">
                      · {((11 - driver.hosDriven) * 60).toFixed(0)}m left
                    </span>}
                </span>
              </div>
              <div className="h-1.5 bg-slate-900 rounded-full overflow-hidden border border-slate-800" data-id="element-234">
                <motion.div initial={{
                width: 0
              }} animate={{
                width: `${Math.min(hosPercent, 100)}%`
              }} className={`h-full rounded-full ${isHosCritical ? 'bg-brand-red' : hosPercent > 80 ? 'bg-brand-amber' : 'bg-brand-green'}`} data-id="element-235" />
              </div>
            </div>

            {isOffline && <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 flex items-start gap-3 mt-3" data-id="element-236">
                <ShieldAlert className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" data-id="element-237" />
                <div data-id="element-238">
                  <p className="text-xs font-medium text-slate-300" data-id="element-239">
                    Data Stale
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-240">
                    Last GPS ping: {driver.lastPing}. ETAs are estimated based
                    on last known position.
                  </p>
                </div>
              </div>}
          </div>

          {/* Active Alerts */}
          {alerts.length > 0 && <div className="p-3 space-y-2" data-id="element-241">
              {alerts.map(alert => {
            // If the driver isn't in critical/warning state, show alert as blue/info
            const effectiveSeverity = driver.status === 'critical' || driver.status === 'warning' ? alert.severity : 'info';
            return <div key={alert.id} className={`px-3 py-2.5 rounded-lg border ${effectiveSeverity === 'critical' ? 'bg-brand-red/10 border-brand-red/30' : effectiveSeverity === 'warning' ? 'bg-brand-amber/10 border-brand-amber/30' : 'bg-brand-blue/10 border-brand-blue/30'}`} data-id="element-242">
                    <div className="flex items-start gap-2.5" data-id="element-243">
                      <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${effectiveSeverity === 'critical' ? 'text-brand-red' : effectiveSeverity === 'warning' ? 'text-brand-amber' : 'text-brand-blue'}`} data-id="element-244" />
                      <div className="flex-1 min-w-0" data-id="element-245">
                        <div className="flex items-center justify-between gap-2" data-id="element-246">
                          <h4 className="text-xs font-semibold truncate text-slate-100" data-id="element-247">
                            {alert.title}
                          </h4>
                          {alert.timeContext && <span className={`text-[11px] font-semibold font-mono shrink-0 ${effectiveSeverity === 'critical' || effectiveSeverity === 'warning' ? 'text-white' : 'text-slate-500'}`} data-id="element-248">
                              {alert.timeContext}
                            </span>}
                        </div>
                        <p className="text-[11px] text-slate-400 leading-snug mt-1 line-clamp-2" data-id="element-249">
                          {alert.description}
                        </p>
                        {(effectiveSeverity === 'critical' || effectiveSeverity === 'warning') && alert.actionHint && alert.actionHint.toLowerCase() !== 'no action needed' && <div className="mt-2" data-id="element-250">
                              {confirmDismissAlertId === alert.id ? <div className="flex items-center justify-between bg-slate-800/80 rounded-md p-2 border border-slate-700" data-id="element-251">
                                  <span className="text-[11px] text-slate-300 font-medium" data-id="element-252">
                                    <strong className="inline-block mr-1 font-medium" data-id="element-253">
                                      Dismiss Alert?
                                    </strong>
                                  </span>
                                  <div className="flex gap-1.5" data-id="element-254">
                                    <button onClick={e => {
                          e.stopPropagation();
                          onDismissAlert?.(alert.id);
                          setConfirmDismissAlertId(null);
                        }} className="px-2.5 py-1 rounded bg-brand-red hover:bg-brand-red/90 text-white text-[10px] font-semibold transition-colors" data-id="element-255">
                                      Yes
                                    </button>
                                    <button onClick={e => {
                          e.stopPropagation();
                          setConfirmDismissAlertId(null);
                        }} className="px-2.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-200 text-[10px] font-semibold transition-colors" data-id="element-256">
                                      No
                                    </button>
                                  </div>
                                </div> : <div className="flex gap-2 relative" data-id="element-257">
                                  <button onClick={() => onContactDriver?.(driver.id)} className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[10px] font-semibold transition-colors ${effectiveSeverity === 'critical' ? 'bg-brand-red hover:bg-brand-red/90 text-white' : 'bg-brand-amber hover:bg-brand-amber/90 text-slate-900'}`} data-id="element-258">
                                    <Phone className="w-3 h-3" data-id="element-259" />
                                    Contact
                                  </button>
                                  <button onClick={() => alert.type === 'hos' ? setIsRerouteOpen(true) : setIsSuggestRouteOpen(true)} className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[10px] font-semibold transition-colors ${effectiveSeverity === 'critical' ? 'bg-brand-red hover:bg-brand-red/90 text-white' : 'bg-brand-amber hover:bg-brand-amber/90 text-slate-900'}`} data-id="element-260">
                                    {alert.type === 'hos' ? <>
                                        <ListOrdered className="w-3 h-3" data-id="element-261" /> Edit
                                        Stops
                                      </> : <>
                                        <Navigation className="w-3 h-3" data-id="element-262" />{' '}
                                        Reroute
                                      </>}
                                  </button>
                                  <div className="relative" data-id="element-263">
                                    <button onClick={e => {
                          e.stopPropagation();
                          setOpenDropdownAlertId(openDropdownAlertId === alert.id ? null : alert.id);
                        }} className="w-[28px] h-[28px] flex items-center justify-center rounded-md hover:bg-white/10 text-slate-200 transition-colors bg-[#33415300] border border-white/50" data-id="element-264">
                                      <MoreVertical className="w-4 h-4" data-id="element-265" />
                                    </button>
                                    {openDropdownAlertId === alert.id && <div className="absolute right-0 top-full mt-1 w-36 bg-slate-800 border border-slate-700 rounded-md shadow-xl z-50 overflow-hidden" data-id="element-266">
                                        <button onClick={e => {
                            e.stopPropagation();
                            onResolveAlert?.(alert.id);
                            setOpenDropdownAlertId(null);
                          }} className="w-full text-left px-3 py-2 text-[11px] text-slate-200 hover:bg-slate-700 transition-colors" data-id="element-267">
                                          Log as Resolved
                                        </button>
                                        <button onClick={e => {
                            e.stopPropagation();
                            setConfirmDismissAlertId(alert.id);
                            setOpenDropdownAlertId(null);
                          }} className="w-full text-left px-3 py-2 text-[11px] text-brand-red hover:bg-slate-700 transition-colors" data-id="element-268">
                                          Dismiss
                                        </button>
                                      </div>}
                                  </div>
                                </div>}
                            </div>}
                      </div>
                    </div>
                  </div>;
          })}
            </div>}

          {/* Route List */}
          <div ref={routeRef} className="p-5" data-id="element-269">
            <h3 className="text-sm font-medium text-slate-300 flex items-center mb-4" data-id="element-270">
              <span className="flex items-center gap-2" data-id="element-271">
                <MapPin className="w-4 h-4 text-slate-500" data-id="element-272" />
                Stops
              </span>
              <span className="ml-auto text-[11px] text-slate-500 font-normal" data-id="element-273">
                {driver.route.filter(s => s.status !== 'completed').length} /{' '}
                {driver.route.length} Remaining
              </span>
            </h3>

            <div className="space-y-0 relative" data-id="element-274">
              <div className="absolute top-3 bottom-8 left-[11px] w-0.5 bg-slate-800" data-id="element-275" />

              {driver.route.map((stop, idx) => {
              const nextStopIdx = driver.route.findIndex(s => s.status !== 'completed');
              const isNextStop = idx === nextStopIdx;
              const nextStopColor = driver.status === 'critical' ? 'brand-red' : driver.status === 'warning' ? 'brand-amber' : 'white';
              return <div key={stop.id} className="relative flex gap-4 pb-6 last:pb-0" data-id="element-276">
                    <div className="relative z-10 mt-1" data-id="element-277">
                      {stop.status === 'completed' ? <div className="w-6 h-6 rounded-full flex items-center justify-center bg-brand-green text-[10px] font-bold text-white" data-id="element-278">
                          {idx + 1}
                        </div> : isNextStop ? <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 bg-slate-950 text-[10px] font-bold ${driver.status === 'critical' ? 'border-brand-red text-brand-red' : driver.status === 'warning' ? 'border-brand-amber text-brand-amber' : 'border-white text-white'}`} data-id="element-279">
                          {idx + 1}
                        </div> : <div className="w-6 h-6 rounded-full flex items-center justify-center border-2 bg-slate-950 border-slate-600 text-[10px] font-bold text-slate-500" data-id="element-280">
                          {idx + 1}
                        </div>}
                    </div>

                    {(() => {
                  const delivery = stopDeliveryMap.get(stop.address);
                  const isHighlighted = delivery && selectedDeliveryRef === delivery.refNumber;
                  const isCriticalNext = stop.status !== 'completed' && driver.status === 'critical';
                  const statusBadge = isCriticalNext ? 'text-brand-red border-brand-red/30 bg-brand-red/10' : stop.status === 'at-risk' ? 'text-brand-amber border-brand-amber/30 bg-brand-amber/10' : stop.status === 'completed' ? 'text-brand-green border-brand-green/30 bg-brand-green/10' : 'text-slate-400 border-slate-700 bg-slate-800';
                  return <div className={`flex-1 rounded-lg p-3 border transition-all ${isHighlighted ? 'bg-slate-800/60 border-slate-600 shadow-[0_0_15px_rgba(0,0,0,0.2)]' : 'bg-slate-900/50 border-slate-800'}`} data-id="element-281">
                          {delivery && <div className="flex items-center justify-between mb-1" data-id="element-282">
                              <span className={`text-[10px] font-mono font-semibold tracking-wide ${stop.status === 'completed' ? 'text-slate-600' : 'text-slate-100'}`} data-id="element-283">
                                {delivery.refNumber}
                              </span>
                              {(stop.status !== 'pending' || isCriticalNext) && <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${statusBadge}`} data-id="element-284">
                                  {isCriticalNext ? 'CRITICAL' : stop.status === 'at-risk' ? 'AT RISK' : stop.status === 'completed' ? 'DONE' : stop.status.toUpperCase()}
                                </span>}
                            </div>}
                          <div className="mb-1" data-id="element-285">
                            <p className={`text-sm font-medium leading-snug ${stop.status === 'completed' ? 'text-slate-500' : 'text-slate-200'}`} data-id="element-286">
                              {stop.address}
                            </p>
                            <p className={`text-[11px] ${stop.status === 'completed' ? 'text-slate-600' : 'text-slate-400'}`} data-id="element-287">
                              Richmond, VA
                            </p>
                          </div>
                          {delivery && stop.status !== 'completed' && <div className="flex items-center gap-1.5 mb-1.5" data-id="element-288">
                              <PackageIcon className="w-3 h-3 text-slate-600" data-id="element-289" />
                              <span className="text-[11px] text-slate-400 truncate" data-id="element-290">
                                {delivery.commodity}
                              </span>
                              <span className="text-[10px] text-slate-600" data-id="element-291">
                                ·
                              </span>
                              <span className="text-[10px] text-slate-500 shrink-0" data-id="element-292">
                                {delivery.pallets}p /{' '}
                                {(delivery.weight / 1000).toFixed(1)}k lbs
                              </span>
                            </div>}
                          {stop.status === 'completed' ? <div className="flex justify-between items-center text-xs" data-id="element-293">
                              <span className="font-mono text-slate-500" data-id="element-294">
                                {(() => {
                          // Compute deterministic ARR/DEP from ETA
                          const [h, m] = stop.eta.replace('~', '').split(':').map(Number);
                          const arrMin = h * 60 + m - (idx * 7 + 3) % 12;
                          const depMin = arrMin + 12 + idx * 5 % 18;
                          const arrH = Math.floor(arrMin / 60) % 24;
                          const arrM = arrMin % 60;
                          const depH = Math.floor(depMin / 60) % 24;
                          const depM = depMin % 60;
                          return `ARR: ${String(arrH).padStart(2, '0')}:${String(arrM).padStart(2, '0')}    DEP: ${String(depH).padStart(2, '0')}:${String(depM).padStart(2, '0')}`;
                        })()}
                              </span>
                              {(() => {
                        // Determine on-time status from ETA vs window
                        const [h, m] = stop.eta.replace('~', '').split(':').map(Number);
                        const etaMin = h * 60 + m;
                        const windowStart = stop.window.split(' - ')[0];
                        const [wh, wm] = windowStart.split(':').map(Number);
                        const windowMin = wh * 60 + wm;
                        const diff = etaMin - windowMin;
                        const status = diff < 5 ? 'Early' : diff > 90 ? 'Late' : 'On Time';
                        const color = status === 'Late' ? 'text-brand-amber' : 'text-brand-green';
                        return <span className={`flex items-center gap-1 text-[10px] font-medium ${color}`} data-id="element-295">
                                    <Clock className="w-3 h-3" data-id="element-296" />
                                    {status}
                                  </span>;
                      })()}
                            </div> : <div className="flex justify-between items-center text-xs" data-id="element-297">
                              <div className="flex items-center gap-2" data-id="element-298">
                                <span className={`font-mono ${isCriticalNext ? 'text-brand-red font-semibold' : stop.status === 'at-risk' ? 'text-brand-amber font-semibold' : 'text-slate-400'}`} data-id="element-299">
                                  ETA: {stop.eta}
                                </span>
                                <span className="text-slate-600" data-id="element-300">·</span>
                                <span className="text-slate-500 font-mono" data-id="element-301">
                                  {stop.distance || (() => {
                            const pendingStops = driver.route.filter((s, i) => i >= idx && s.status !== 'completed');
                            const posInPending = pendingStops.indexOf(stop);
                            const base = (driver.id.charCodeAt(driver.id.length - 1) * 7 + idx * 11) % 20 + 5 + posInPending * 8;
                            return `${base} mi`;
                          })()}
                                </span>
                              </div>
                              <span className="text-slate-500" data-id="element-302">
                                {stop.window !== 'N/A' ? `Window: ${stop.window}` : ''}
                              </span>
                            </div>}
                        </div>;
                })()}
                  </div>;
            })}
            </div>
          </div>

          {/* Cargo Manifest */}
          {driver.cargo.length > 0 && <CargoManifest cargo={driver.cargo} data-id="element-303" />}
        </div>
      </div>

      {/* Action Strip */}
      <div className="p-4 border-t border-slate-800 bg-slate-900/50 grid grid-cols-3 gap-2 shrink-0 relative z-20" data-id="element-304">
        <button onClick={() => onContactDriver?.(driver.id)} className="flex items-center justify-center gap-1.5 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium rounded-lg transition-colors" data-id="element-305">
          <Phone className="w-3.5 h-3.5" data-id="element-306" /> Contact
        </button>
        <button onClick={() => setIsSuggestRouteOpen(!isSuggestRouteOpen)} className={`flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium rounded-lg transition-colors ${isSuggestRouteOpen ? 'bg-brand-blue/20 border border-brand-blue/30 text-brand-blue' : 'bg-slate-800 hover:bg-slate-700 text-slate-200'}`} data-id="element-307">
          <Navigation className="w-3.5 h-3.5" data-id="element-308" /> Reroute
        </button>
        <button onClick={() => setIsRerouteOpen(!isRerouteOpen)} className={`flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium rounded-lg transition-colors ${isRerouteOpen ? 'bg-brand-blue/20 border border-brand-blue/30 text-brand-blue' : 'bg-slate-800 hover:bg-slate-700 text-slate-200'}`} data-id="element-309">
          <ListOrdered className="w-3.5 h-3.5" data-id="element-310" /> Edit Stops
        </button>
      </div>

      {/* ReroutePanel now overlays the entire DriverDetail including the action strip */}
      <AnimatePresence data-id="element-311">
        {isRerouteOpen && <ReroutePanel driver={driver} onClose={() => setIsRerouteOpen(false)} onSave={updatedStops => {
        onUpdateRoute?.(driver.id, updatedStops);
      }} onPreview={stops => {
        onPreviewRoute?.(driver.id, stops);
      }} onCancel={() => {
        onCancelRoutePreview?.(driver.id);
      }} data-id="element-312" />}
        {isSuggestRouteOpen && <SuggestRoutePanel driver={driver} onClose={() => setIsSuggestRouteOpen(false)} onApply={route => {
        if (route && onApplyReroute) {
          onApplyReroute(driver.id, {
            etaImpactMin: route.etaImpactMin,
            label: route.label
          });
        }
        setIsSuggestRouteOpen(false);
      }} onHoverRoute={onHoverRoute} customSuggestions={customSuggestions} data-id="element-313" />}
      </AnimatePresence>
    </motion.div>;
}
function StatusBadge({
  status
}: {
  status: Severity;
}) {
  const config = {
    critical: {
      color: 'text-brand-red',
      bg: 'bg-brand-red/10',
      border: 'border-brand-red/30',
      label: 'OFF TRACK'
    },
    warning: {
      color: 'text-brand-amber',
      bg: 'bg-brand-amber/10',
      border: 'border-brand-amber/30',
      label: 'AT RISK'
    },
    offline: {
      color: 'text-slate-400',
      bg: 'bg-slate-800',
      border: 'border-slate-600',
      label: 'OFFLINE'
    },
    info: {
      color: 'text-brand-blue',
      bg: 'bg-brand-blue/10',
      border: 'border-brand-blue/30',
      label: 'INFO'
    },
    normal: {
      color: 'text-brand-green',
      bg: 'bg-brand-green/10',
      border: 'border-brand-green/30',
      label: 'ON TRACK'
    }
  }[status];
  return <span className={`text-[10px] font-bold px-2 py-1 rounded border tracking-wider ${config.color} ${config.bg} ${config.border}`} data-id="element-314">
      {config.label}
    </span>;
}
function SignalBars({
  bars
}: {
  bars: number;
}) {
  if (bars === 0) {
    return <img src="https://cdn.magicpatterns.com/uploads/qtiWE7RgqX6uR9cCddEQgD/android_cell_4_bar_off_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24.svg" alt="No signal" className="w-4 h-4 ml-0.5 opacity-50" data-id="element-315" />;
  }
  const heights = [4, 7, 10, 13];
  return <svg width="14" height="14" viewBox="0 0 14 14" className="ml-0.5" data-id="element-316">
      {heights.map((h, i) => <rect key={i} x={i * 3.5} y={14 - h} width="2.5" height={h} rx="0.5" fill={i < bars ? '#cbd5e1' : '#334155'} data-id="element-317" />)}
    </svg>;
}
function CargoManifest({
  cargo
}: {
  cargo: CargoItem[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  if (cargo.length === 0) return null;
  return <div className="p-5 border-t border-slate-800" data-id="element-318">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between cursor-pointer" data-id="element-319">
        <div data-id="element-320">
          <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2" data-id="element-321">
            <PackageIcon className="w-4 h-4 text-slate-500" data-id="element-322" />
            Cargo Manifest
          </h3>
          <p className="text-[10px] text-slate-500 mt-1" data-id="element-323">
            {cargo.reduce((sum, c) => sum + c.pallets, 0)} pallets ·{' '}
            {(cargo.reduce((sum, c) => sum + c.weight, 0) / 1000).toFixed(1)}k
            lbs total
          </p>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-324" />
      </button>
      {isOpen && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: 'auto'
    }} className="space-y-2 mt-4 overflow-hidden" data-id="element-325">
          {cargo.map(item => <div key={item.id} className="bg-slate-900/50 border border-slate-800 rounded-lg p-3" data-id="element-326">
              <div className="flex items-center justify-between mb-1.5" data-id="element-327">
                <span className="text-[10px] font-mono text-slate-500" data-id="element-328">
                  {item.bol}
                </span>
                <span className={`text-[10px] font-bold uppercase tracking-wider ${item.status === 'delivered' ? 'text-brand-green' : 'text-slate-400'}`} data-id="element-329">
                  {item.status === 'in-transit' ? 'IN TRANSIT' : item.status.toUpperCase()}
                </span>
              </div>
              <p className="text-xs font-medium mb-1.5 text-slate-200" data-id="element-330">
                {item.commodity}
              </p>
              <div className="flex gap-3 text-[10px] text-slate-500" data-id="element-331">
                <span data-id="element-332">{item.weight.toLocaleString()} lbs</span>
                <span data-id="element-333">·</span>
                <span data-id="element-334">
                  {item.pallets} {item.pallets === 1 ? 'pallet' : 'pallets'}
                </span>
              </div>
            </div>)}
        </motion.div>}
    </div>;
}